/**
 * Security utility functions for the BOBLEGOB platform
 * Handles input sanitization, XSS prevention, and emoji validation
 */

/**
 * Escapes HTML characters to prevent XSS attacks
 * @param {string} unsafeText - The potentially unsafe text to escape
 * @returns {string} The escaped text
 */
export const escapeHtml = (unsafeText) => {
  if (typeof unsafeText !== 'string') return '';
  
  return unsafeText
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

/**
 * Validates and sanitizes emoji input
 * @param {string} emoji - The emoji to validate
 * @returns {boolean} True if the input is a valid emoji, false otherwise
 */
export const isValidEmoji = (emoji) => {
  if (typeof emoji !== 'string' || emoji.length > 4) return false;
  
  // Allowed emoji list - add more as needed
  const allowedEmojis = [
    '🔥', '💸', '😍', '😂', '💡', '🎨', '🤔', // Main reaction emojis
    '🤮', '😊', '👍', '👎', '❤️', '🎉', '👏', '🙌', 
    '😢', '😎', '🤩', '😋', '🤯', '😱', '👀', '💯', '🌟', '⭐', '✨'
  ];
  
  return allowedEmojis.includes(emoji);
};

/**
 * Decodes HTML entities to display text properly
 * @param {string} encodedText - The HTML-encoded text to decode
 * @returns {string} The decoded text
 */
export const decodeHtml = (encodedText) => {
  if (typeof encodedText !== 'string') return '';
  
  return encodedText
    .replace(/&#039;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&'); // This should be last to avoid double-decoding
};

/**
 * Sanitizes text content for display
 * @param {string} text - Text to sanitize
 * @returns {string} Sanitized text
 */
export const sanitizeText = (text) => {
  if (typeof text !== 'string') return '';
  
  // First decode HTML entities that might be in the database
  const decoded = decodeHtml(text);
  
  // Then check for dangerous content
  if (!containsOnlyValidContent(decoded)) {
    return ''; // Return empty string if dangerous content is detected
  }
  
  return decoded;
};

/**
 * Sanitize emoji reactions to prevent XSS
 * @param {Object} reactions - Object containing emoji reactions
 * @returns {Object} Sanitized reactions object
 */
export const sanitizeReactions = (reactions) => {
  if (!reactions || typeof reactions !== 'object') return {};
  
  const sanitized = {};
  Object.keys(reactions).forEach(emoji => {
    if (isValidEmoji(emoji)) {
      sanitized[emoji] = reactions[emoji];
    }
  });
  
  return sanitized;
};

/**
 * Verify if a string contains only emojis and not scripts or HTML
 * @param {string} input - Input to check
 * @returns {boolean} Whether the input contains only valid content
 */
export const containsOnlyValidContent = (input) => {
  if (typeof input !== 'string') return false;
  
  // Check for potentially dangerous HTML/script content
  const dangerousPatterns = [
    /<script/i,
    /<\/script>/i,
    /<img/i,
    /<iframe/i,
    /<style/i,
    /<link/i,
    /<object/i,
    /<embed/i,
    /<on\w+=/i,  // Detect event handlers like onclick=
    /javascript:/i,
    /data:/i,     // Detect data URIs
    /vbscript:/i
  ];
  
  return !dangerousPatterns.some(pattern => pattern.test(input));
};

/**
 * Sanitizes HTML content to allow only safe tags and attributes
 * @param {string} html - HTML content to sanitize
 * @returns {string} Sanitized HTML content
 */
export const sanitizeHtml = (html) => {
  if (typeof html !== 'string') return '';
  
  // First check for dangerous patterns
  if (!containsOnlyValidContent(html)) {
    return escapeHtml(html); // Return escaped version if dangerous
  }
  
  // Convert className to class for HTML rendering
  let processedHtml = html.replace(/className=/g, 'class=');
  
  // Allow only specific safe tags and attributes
  const allowedTags = ['span', 'strong', 'em', 'b', 'i', 'u', 'br', 'p', 'div', 'font'];
  const allowedAttributes = ['class', 'style', 'color', 'size'];
  
  // Simple but effective sanitization
  let sanitized = processedHtml;
  
  // Remove any tags not in the allowed list and sanitize attributes
  const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^<>]*>/g;
  sanitized = sanitized.replace(tagRegex, (match, tagName) => {
    if (allowedTags.includes(tagName.toLowerCase())) {
      // Keep the tag but sanitize attributes
      return sanitizeTagAttributes(match, allowedAttributes);
    }
    return ''; // Remove disallowed tags
  });
  
  return sanitized;
};

/**
 * Sanitizes attributes within HTML tags
 * @param {string} tag - The HTML tag to sanitize
 * @param {Array} allowedAttributes - Array of allowed attribute names
 * @returns {string} Sanitized tag
 */
const sanitizeTagAttributes = (tag, allowedAttributes) => {
  // Extract tag name and attributes
  const tagMatch = tag.match(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b/);
  if (!tagMatch) return tag;
  
  const tagName = tagMatch[1];
  const isClosingTag = tag.startsWith('</');
  
  if (isClosingTag) {
    return `</${tagName}>`;
  }
  
  // Extract and sanitize attributes using a more reliable approach
  const attributes = [];
  
  // Find all attribute patterns: attr="value" or attr='value'
  const attrPattern = /(\w+)=["']([^"']*)["']/g;
  let match;
  
  while ((match = attrPattern.exec(tag)) !== null) {
    const attrName = match[1];
    const attrValue = match[2];
    
    if (allowedAttributes.includes(attrName.toLowerCase())) {
      // Sanitize attribute value
      const sanitizedValue = attrValue
        .replace(/[<>]/g, '') // Remove < and >
        .replace(/javascript:/gi, '') // Remove javascript: protocol
        .replace(/on\w+=/gi, ''); // Remove event handlers
      
      if (sanitizedValue) {
        attributes.push(`${attrName}="${sanitizedValue}"`);
      }
    }
  }
  
  const attributesString = attributes.length > 0 ? ' ' + attributes.join(' ') : '';
  return `<${tagName}${attributesString}>`;
};